import { CommonModule, DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  Inject,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { EncrDecrService } from '@mysrc/app/Providers/EncrDecrService';
import { BaseHttpReqRespService } from '@mysrc/app/modules/fwcore/services';
import { IsgGridComponent } from '@mysrc/app/shared/components/isg-grid';
import { IsgGridConfig } from '@mysrc/app/shared/models/grid-config.model';
import { dateValueFormatter } from '@mysrc/app/shared/utils/date-formatter';
import { NgbModal, NgbModalRef, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { GridModule } from '@progress/kendo-angular-grid';
import * as XLSX from 'xlsx';
//import * as Excel from 'exceljs/dist/exceljs.min.js';
// ✅ New (Angular 17 way)
//import { Workbook } from 'exceljs';
//import { saveAs } from 'file-saver';
// ✅ Add these two lines 
import { Workbook, WorkbookSheet, WorkbookSheetRow, WorkbookSheetRowCell } from '@progress/kendo-angular-excel-export';
import { saveAs } from '@progress/kendo-file-saver';

import { CommonService } from '../../../question/services/commonservice';
import { UserService } from '../../services/user.service';
// Make sure ALL these types are in your import at the top of the file:
import {
  User,
  Role,
  Company,
  ServiceLine,
  Survey,
  SaveUserResponse,
  MailContent
} from '../../models/user.model';

@Component({
  selector: 'user',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, GridModule, NgbModule, AgGridAngular, IsgGridComponent],
  templateUrl: './page-user.component.html',
  styleUrls: ['./page-user.component.css'],
  providers: [CommonService, UserService, DatePipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class PageUserComponent extends BaseHttpReqRespService implements OnInit {

  // ── ViewChild modal refs (Settings pattern) ────────────────
  @ViewChild('userModal') userModal!: TemplateRef<any>;
  @ViewChild('deleteUserModal') deleteUserModal!: TemplateRef<any>;
  @ViewChild('bulkUploadModal') bulkUploadModal!: TemplateRef<any>;
  @ViewChild('newsletterModal') newsletterModal!: TemplateRef<any>;
  @ViewChild('oktaUserModal') oktaUserModal!: TemplateRef<any>;
  @ViewChild('oktaActivateUserModal') oktaActivateUserModal!: TemplateRef<any>;

  modalRef: NgbModalRef | undefined;

  // ── Forms ──────────────────────────────────────────────────
  userForm!: FormGroup;
  bulkUserUploadForm!: FormGroup;
  newsLetterUploadForm!: FormGroup;
  oktaUserForm!: FormGroup;
  oktaActivateUserForm!: FormGroup;

  // ── Grid data ──────────────────────────────────────────────
  public usernewlist: User[] = [];
  public providerUserList: User[] = [];
  public roles: Role[] = [];
  public company: Company[] = [];
  public serviceLines: ServiceLine[] = [];
  public surveys: Survey[] = [];

  // ── State ──────────────────────────────────────────────────
  public currentUserId: number = -1;
  public currentDeleteId: number = 0;
  public tmpWorkspaceId: string = '';
  public successOrFailureMessage: string = '';
  public successOrFailureOktaMessage: string = '';
  public myAppUrl: any;
  private gridApi: any;

  // ── File upload ────────────────────────────────────────────
  public fileControlnewLetter: FileList | null = null;
  public isFileInvalid: boolean = false;
  file: File | null = null;
  fileName: string = 'No File Chosen';
  arrayBuffer: any;

  // ── ISG Grid config (Settings pattern) ────────────────────
  gridConfig: Partial<IsgGridConfig> = {
    height: '500px',
    pagination: true,
    pageSize: 10,
    autoSizeColumns: true
  };

  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true
  };

  // ── Column definitions (typed ColDef[] — Settings pattern) ─
  columnDefs: ColDef[] = [
    { headerName: 'User ID', field: 'userId', hide: true },
    { headerName: 'First Name', field: 'userFirstName', resizable: true },
    { headerName: 'Last Name', field: 'userLastName', resizable: true },
    { headerName: 'Email Address', field: 'userEmail', resizable: true },
    { headerName: 'Company', field: 'providerCompany', resizable: true },
    { headerName: 'Role', field: 'role', resizable: true },
    { headerName: 'Status', field: 'isActive', resizable: true },
    {
      headerName: 'Edit',
      cellRenderer: (params: any) => this.linkRenderer(params)
    },
    {
      headerName: 'Delete',
      width: 100,
      cellRenderer: (params: any) => this.deleteImageRenderer(params)
    }
  ];

  constructor(
    @Inject(OKTA_AUTH) private oauth: OktaAuth,
    protected http: HttpClient,
    private EncrDecr: EncrDecrService,
    private _router: Router,
    private formBuilder: FormBuilder,
    private renderer: Renderer2,
    private modalService: NgbModal,
    private userService: UserService,
    @Inject('BASE_URL') baseUrl: string
  ) {
    super(http, oauth);
    this.myAppUrl = baseUrl;
  }

  ngOnInit(): void {
    debugger;
    this.tmpWorkspaceId = this.EncrDecr.get(localStorage.getItem('workspaceId') ?? '');
    this.initForms();
    this.getUserListDetails();
    this.loadDropdowns();
  }

  // ── Form initialisation ────────────────────────────────────
  private initForms(): void {
    const createdByUserId = this.EncrDecr.get(localStorage.getItem('userId') ?? '');

    this.userForm = this.formBuilder.group({
      userId: new FormControl(0, Validators.required),
      userEmail: new FormControl('', [Validators.email, Validators.required, this.userExistsValidator()]),
      firstName: new FormControl('', [Validators.required, this.noWhitespaceValidator]),
      middleName: new FormControl(''),
      lastName: new FormControl(''),
      roleId: new FormControl('', Validators.required),
      companyId: new FormControl('', Validators.required),
      notifyMe: new FormControl(false),
      createdBy: new FormControl(createdByUserId)
    });

    this.oktaUserForm = this.formBuilder.group({
      oktaCompanyId: new FormControl('', Validators.required),
      userEmail: new FormControl(null, Validators.required)
    });

    this.oktaActivateUserForm = this.formBuilder.group({
      oktaCompanyId: new FormControl('', Validators.required),
      userEmail: new FormControl(null, Validators.required)
    });

    this.newsLetterUploadForm = this.formBuilder.group({
      subject: new FormControl(null, Validators.required),
      fileControlnewLetter: new FormControl(null, Validators.required),
      emailtemplate: new FormControl('', Validators.required)
    });

    this.bulkUserUploadForm = this.formBuilder.group({
      serviceLineId: new FormControl(0, Validators.required),
      surveyId: new FormControl(0, Validators.required),
      fileControl: new FormControl(null, Validators.required),
      excludeExistingUsers: new FormControl(false),
      bulkUsers: new FormControl([]),
      createdBy: new FormControl(createdByUserId)
    });
  }

  // ── Load dropdowns ─────────────────────────────────────────
  // ── Fix: add explicit types matching service return types ──
  private loadDropdowns(): void {
    debugger;
    this.userService.getRoles().subscribe({
      next: (result: any[]) => this.roles = result,
      error: (err: unknown) => console.error(err)
    });
    this.userService.getCompanies(this.tmpWorkspaceId).subscribe({
      next: (result: any[]) => this.company = result,
      error: (err: unknown) => console.error(err)
    });
    this.userService.getServiceLines().subscribe({
      next: (result: any[]) => this.serviceLines = result,
      error: (err: unknown) => console.error(err)
    });
  }

  // ── User list ──────────────────────────────────────────────
  getUserListDetails(): void {
    this.userService.getUserListDetails(this.tmpWorkspaceId).subscribe({
      next: (result: User[]) => this.usernewlist = result,
      error: (err: unknown) => console.error(err)
    });
  }

  // ── Grid ready ─────────────────────────────────────────────
  onGridReady(params: any): void {
    this.gridApi = params.api;
    params.api.sizeColumnsToFit();
  }

  // ── Open modals (NgbModal pattern — Settings) ──────────────
  openCreateUserModal(): void {
    const userId = this.EncrDecr.get(localStorage.getItem('userId') ?? '');
    this.currentUserId = -1;
    this.userForm.reset({ userId: -1, notifyMe: false, createdBy: userId });
    this.modalRef = this.modalService.open(this.userModal, {
      backdrop: 'static',
      keyboard: false
    });
  }

  openBulkUploadModal(): void {
    this.bulkUserUploadForm.reset({ excludeExistingUsers: false });
    this.modalRef = this.modalService.open(this.bulkUploadModal, {
      size: 'lg',
      backdrop: 'static'
    });
  }

  openNewsletterModal(): void {
    this.getEmailTemplate();
    this.modalRef = this.modalService.open(this.newsletterModal, {
      size: 'lg',
      backdrop: 'static'
    });
  }

  openOktaUserModal(): void {
    this.oktaUserForm.reset();
    this.modalRef = this.modalService.open(this.oktaUserModal, {
      backdrop: 'static'
    });
  }

  openDeleteUserModal(): void {
    this.modalRef = this.modalService.open(this.deleteUserModal, {
      backdrop: 'static',
      keyboard: false
    });
  }

  // ── Close / reset forms ────────────────────────────────────
  onClose(): void {
    const tmpCreatedById = this.EncrDecr.get(localStorage.getItem('userId') ?? '');
    this.userForm.reset({ createdBy: tmpCreatedById });
  }
  onCloseNewsletter(): void { this.newsLetterUploadForm.reset(); }
  onCloseBulkUpload(): void { this.bulkUserUploadForm.reset(); }

  // ── Save user ──────────────────────────────────────────────
  saveUserList(): void {
    debugger;
    const userPayload = this.buildUserPayload();
    this.userService.saveUser(userPayload).subscribe({
      next: (result: SaveUserResponse) => {
        this.usernewlist = result.userList;
        this.successOrFailureOktaMessage = this.getOktaStatusMessage(result.createOktaUserStatus);
        this.messageTimeout(12000, 'okta');
        this.onClose();
      },
      error: (err: unknown) => console.error(err)
    });
  }

  // ── Payload function ──────────────────────────────────────────────
  private buildUserPayload(): any {
    const formValue = this.userForm.value;
    return {
      userId: formValue.userId ?? 0,
      userName: formValue.userName ?? '',
      userEmail: formValue.userEmail ?? '',
      providerCompany: formValue.providerCompany ?? '',
      role: formValue.role ?? '',
      roleId: formValue.roleId ?? 0,
      companyId: formValue.companyId ?? 0,
      firstName: formValue.firstName ?? '',
      middleName: formValue.middleName ?? '',
      lastName: formValue.lastName ?? '',
      password: formValue.password ?? '',
      emailConfirmation: formValue.emailConfirmation ?? '',
      notifyMe: formValue.notifyMe ?? false,
      passwordSalt: formValue.passwordSalt ?? '',
      roleName: formValue.roleName ?? '',
      companyName: formValue.companyName ?? '',
      isActive: formValue.isActive ?? true,
      createdBy: formValue.createdBy ?? 0,
      exceptionalSurveyId: formValue.exceptionalSurveyId ?? 0,
      mappedServiceLineId: formValue.mappedServiceLineId ?? 0,
      isExceptionalUser: formValue.isExceptionalUser ?? false,
      workSpaceId: formValue.workSpaceId ?? 0,
      workSpaceLogo: formValue.workSpaceLogo ?? '',
      workspaceName: formValue.workspaceName ?? ''
    };
  }

  // ── Delete user ────────────────────────────────────────────
  deleteUser(): void {
    this.userService.deleteUser(this.currentDeleteId, this.tmpWorkspaceId).subscribe({
      next: (result: User[]) => {
        if (result.length > 0) this.usernewlist = result;
      },
      error: (err: unknown) => console.error(err)
    });
  }

  // ── Okta user ──────────────────────────────────────────────
  saveOktaUser(): void {
    debugger;
    // Build payload: include generated password (not shown in the UI)
    const form = this.oktaUserForm.value;
    const payload = {
      oktaCompanyId: form.oktaCompanyId,
      userEmail: form.userEmail,
      oktaPassword: '' //this.generateOktaPassword()
    };
    this.userService.saveOktaUser(payload).subscribe({
      next: (result: string) => {
        this.successOrFailureOktaMessage = this.getOktaStatusMessage(result);
        this.oktaUserForm.reset();
        this.messageTimeout(12000, 'okta');
      },
      error: (err: unknown) => console.error(err)
    });
  }

  // secure random password generator (12 chars: upper/lower/digits/symbols)
  //private generateOktaPassword(length = 12): string {
  //  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()-_=+';
  //  const array = new Uint32Array(length);
  //  // window.crypto is available in browsers for secure randomness
  //  (window.crypto || (window as any).msCrypto).getRandomValues(array);
  //  return Array.from(array).map(n => chars[n % chars.length]).join('');
  //}

  activateOktaUser(): void {
    this.userService.activateOktaUser(this.oktaActivateUserForm.value).subscribe({
      next: (result: string) => {
        this.successOrFailureOktaMessage = this.getOktaActivateStatusMessage(result);
        this.oktaActivateUserForm.reset();
        this.messageTimeout(12000, 'okta');
      },
      error: (err: unknown) => console.error(err)
    });
  }

  // ── Company change → filter provider users (replaces linq-es5) ──
  onCompanyChange(): void {
    const companyId = this.oktaUserForm.value.oktaCompanyId;
    // ✅ Native .filter() replaces linq-es5 Enumerable
    this.providerUserList = this.usernewlist.filter(u => u.companyId === companyId);
  }

  onActivateOktaCompanyChange(): void {
    const companyId = this.oktaActivateUserForm.value.oktaCompanyId;
    this.providerUserList = this.usernewlist.filter(u => u.companyId === companyId);
  }

  onServiceLineChange(): void {
    this.userService.getSurveysByServiceLine(
      this.bulkUserUploadForm.value.serviceLineId
    ).subscribe({
      next: (result: Survey[]) => this.surveys = result,
      error: (err: unknown) => console.error(err)
    });
  }

  // ── Newsletter ─────────────────────────────────────────────
  getEmailTemplate(): void {
    this.userService.getEmailTemplate(this.tmpWorkspaceId).subscribe({
      next: (result: MailContent) => {
        this.newsLetterUploadForm.patchValue({
          fileControlnewLetter: '',
          subject: '',
          emailtemplate: result.mailBody
        });
      },
      error: (err: unknown) => console.error(err)
    });
  }

  selectFile(event: Event, form: FormGroup): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) {
      this.isFileInvalid = true;
    } else {
      this.isFileInvalid = false;
      this.fileControlnewLetter = input.files;
    }
  }

  sendNewLetters(form: FormGroup): void {
    if (!this.fileControlnewLetter) return;
    const selectedFile = this.fileControlnewLetter.item(0)!;
    if (selectedFile.size / 1e6 >= 20) {
      this.successOrFailureMessage = 'File size cannot be greater than 20MB.';
      return;
    }
    const formData = new FormData();
    formData.append('file', selectedFile);
    this.userService.sendNewsletter(
      formData,
      form.controls['subject'].value,
      selectedFile.name,
      form.controls['emailtemplate'].value
    ).subscribe({
      next: (_result: unknown) => {
        this.successOrFailureMessage = 'Document sent successfully.';
        this.messageTimeout(3200, 'general');
        this.fileControlnewLetter = null;
      },
      error: (err: unknown) => console.error(err)
    });
  }

  // ── Bulk upload ────────────────────────────────────────────
  incomingFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    this.file = input.files[0];
    this.fileName = this.file.name;
    this.importExcel();
  }

  importExcel(): void {
    if (!this.file) return;
    const fileReader = new FileReader();
    fileReader.onload = () => {
      this.arrayBuffer = fileReader.result;
      const data = new Uint8Array(this.arrayBuffer);
      const arr = Array.from(data).map(d => String.fromCharCode(d));
      const bstr = arr.join('');
      const workbook = XLSX.read(bstr, { type: 'binary' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const bulkUserSheet: any[] = XLSX.utils.sheet_to_json(firstSheet, { raw: true });
      this.bulkUserUploadForm.patchValue({ bulkUsers: bulkUserSheet });
    };
    fileReader.readAsArrayBuffer(this.file);
  }

  saveBulkUser(): void {
    this.userService.saveBulkUsers(this.bulkUserUploadForm.value).subscribe({
      next: (result: any[]) => {
        this.exportToExcel(result);
        this.file = null;
        this.fileName = 'No File Chosen';
        this.bulkUserUploadForm.reset();
        this.successOrFailureMessage = 'Users created successfully.';
        this.messageTimeout(3200, 'general');
        this.getUserListDetails();
      },
      error: (_err: unknown) => {
        this.bulkUserUploadForm.reset();
        this.file = null;
        this.fileName = 'No File Chosen';
        this.successOrFailureMessage = 'Error while importing users.';
        this.messageTimeout(3200, 'general');
      }
    });
  }

  // ── Export master user list ────────────────────────────────
  getMasterUserList(): void {
    this.userService.getMasterUserList(this.tmpWorkspaceId).subscribe({
      next: (result: any[]) => this.exportToExcel(result),
      error: (err: unknown) => console.error(err)
    });
  }

  // ── Excel export (exceljs — same as original) ──────────────
  //exportToExcel(params: any[]): void {
  //  if (!params?.length) return;
  //  const columns = Object.keys(params[0]);

  //  // ✅ Use Workbook directly — no more Excel.Workbook()
  //  const workbook = new Workbook();
  //  const worksheet = workbook.addWorksheet('UserList');

  //  const headerRow = worksheet.addRow(columns);
  //  headerRow.eachCell((cell) => {
  //    cell.fill = {
  //      type: 'pattern',
  //      pattern: 'solid',
  //      fgColor: { argb: 'FFFFFF00' },
  //      bgColor: { argb: 'FF0000FF' }
  //    };
  //    cell.border = {
  //      top: { style: 'thin' }, left: { style: 'thin' },
  //      bottom: { style: 'thin' }, right: { style: 'thin' }
  //    };
  //  });

  //  params.forEach(row => {
  //    worksheet.addRow(columns.map(col => row[col]));
  //  });

  //  workbook.xlsx.writeBuffer().then((data) => {
  //    const blob = new Blob([data], {
  //      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  //    });
  //    saveAs(blob, 'BulkUserList.xlsx');
  //  });
  //}

  //Replacing the existing exceljs and file saver with kendo packages
  exportToExcel(params: any[]): void {
    if (!params?.length) return;

    const columns = Object.keys(params[0]);

    // ── Header row with styling ──────────────────────────────
    const headerCells: WorkbookSheetRowCell[] = columns.map((col: string) => ({
      value: col,
      bold: true,
      background: '#FFFF00',
      borderBottom: { size: 1, color: '#000000' },
      borderTop: { size: 1, color: '#000000' },
      borderLeft: { size: 1, color: '#000000' },
      borderRight: { size: 1, color: '#000000' }
    }));

    // ── Data rows ────────────────────────────────────────────
    const dataRows: WorkbookSheetRow[] = params.map((item: any) => ({
      cells: columns.map((col: string) => ({
        value: item[col] ?? ''
      }))
    }));

    // ── Sheet definition ─────────────────────────────────────
    const sheet: WorkbookSheet = {
      name: 'UserList',
      rows: [
        { cells: headerCells },
        ...dataRows
      ],
      columns: columns.map((col: string) => ({
        width: Math.max(col.length, 15) * 7
      }))
    };

    // ── Build workbook and save ──────────────────────────────
    const workbook = new Workbook({ sheets: [sheet] });

    workbook.toDataURL().then((dataURL: string) => {
      saveAs(dataURL, 'BulkUserList.xlsx');
    });
  }
  // ── Cell renderers (Renderer2 — Settings pattern) ──────────
  linkRenderer(params: any): HTMLElement {
    const element = this.renderer.createElement('a');
    this.renderer.addClass(element, 'fa');
    this.renderer.addClass(element, 'fa-edit');
    this.renderer.setAttribute(element, 'href', 'javascript:void(0)');
    this.renderer.listen(element, 'click', () => {
      this.currentUserId = params.data.userId;
      this.userService.editUser(params.data.userId).subscribe({
        next: (response: User) => {
          this.userForm.patchValue({
            userId: response.userId,
            firstName: response.firstName,
            middleName: response.middleName,
            lastName: response.lastName,
            userEmail: response.userEmail,
            roleId: response.roleId,
            companyId: response.companyId,
            notifyMe: response.notifyMe
          });
          this.modalRef = this.modalService.open(this.userModal, {
            backdrop: 'static',
            keyboard: false
          });
        },
        error: (err: unknown) => console.error(err)
      });
    });
    return element;
  }

  deleteImageRenderer(params: any): HTMLElement {
    const element = this.renderer.createElement('a');
    const icon = this.renderer.createElement('i');
    icon.setAttribute('class', 'fa fa-trash anchor-cursor');
    this.renderer.appendChild(element, icon);
    this.renderer.listen(element, 'click', () => {
      this.currentDeleteId = params.data.userId;
      this.openDeleteUserModal();
    });
    return element;
  }

  // ── Validators ─────────────────────────────────────────────
  public noWhitespaceValidator(control: FormControl): { [key: string]: boolean } | null {
    const isWhitespace = (control.value || '').trim().length === 0;
    return isWhitespace ? { whitespace: true } : null;
  }

  userExistsValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      if (this.currentUserId > 0) return null;
      const exists = this.usernewlist.some(
        u => u.userEmail?.toLowerCase() === control.value?.toLowerCase()
      );
      return exists ? { userExists: true } : null;
    };
  }

  // ── Message helpers (replaces direct DOM manipulation) ─────
  messageTimeout(duration: number, type: 'general' | 'okta'): void {
    window.setTimeout(() => {
      if (type === 'okta') this.successOrFailureOktaMessage = '';
      else this.successOrFailureMessage = '';
    }, duration);
  }

  // ── Okta status message helper (replaces repeated if blocks) ─
  private getOktaStatusMessage(status: string): string {
    const messages: Record<string, string> = {
      '1': 'The user has been created successfully on Okta.',
      '2': 'You are an existing user of ISG applications. Please use your Okta credential to login.',
      '3': 'An error occurred while creating the user on Okta.'
    };
    return messages[status] ?? '';
  }

  private getOktaActivateStatusMessage(status: string): string {
    const messages: Record<string, string> = {
      '1': 'The user does not exist in the Okta database.',
      '2': 'The user has been activated successfully.',
      '3': 'An error occurred while activating the user on Okta.'
    };
    return messages[status] ?? '';
  }
}
